<script>
  import { onMount } from "svelte";
  import { fetchWithSession } from "lib/util/fetchWithSession.js";
  import download from "downloadjs";
  import { anonymizationFieldError } from "lib/util/regex.js";
  import { InfoBlock } from "paxray-components-svelte";
  import Button from "lib/components/Button.svelte";
  import ContentSwitcher from "lib/components/ContentSwitcher.svelte";
  import DefaultUiElementRules from "./DefaultUiElementRules.svelte";
  import DefaultWindowTitleRules from "./DefaultWindowTitleRules.svelte";
  import GlobalAnonRuleEditor from "./GlobalAnonRuleEditor.svelte";
  import SafeTitleList from "./SafeTitleList.svelte";
  import ProjectAnonRules from "./ProjectAnonRules.svelte";
  import SpotlightGuide from "lib/components/common/SpotlightGuide.svelte";
  import { registerViewTour } from "lib/stores/viewTourStore.js";

  export let drafts = { windowtitles: [], elements: [] };
  // True while a built-in tab is active, so Admin can disable the import.
  export let readonly = false;

  // Built-in tabs show the rules the backend pipeline runs before the
  // project rules. Draft tabs keep their export file names.
  const tabs = [
    { label: "Title Allowlist", kind: "safetitles", file: "standard-titles", safe: true },
    { label: "UI Allowlist", kind: "safeelements", file: "ui-allowlist", safe: true },
    { label: "Global Regex Patterns", kind: "windowtitles", file: "entity-patterns", patterns: true },
    { label: "Title Default Rules", kind: "windowtitles", file: "windowtitles" },
    { label: "UI Default Rules", kind: "elements", file: "elements" },
    { label: "Title Project Rules", kind: "windowtitles", file: "project-title-rules", project: true },
    { label: "UI Project Rules", kind: "elements", file: "project-element-rules", project: true },
  ];

  const csvColumns = {
    safeelements: ["applicationname", "type", "elementclass", "input_source", "text", "status"],
    safetitles: ["applicationname", "windowtitle", "status", "source", "created_by", "created_when"],
    windowtitles: ["id", "name", "applicationname", "search", "replace"],
    elements: [
      "id",
      "name",
      "applicationname",
      "elementclass",
      "type",
      "namesearch",
      "namereplace",
      "automationsearch",
      "automationreplace",
    ],
  };

  let selected = 3;
  let safeTitles = [];
  let safeElements = [];
  let defaultsLoading = true;
  let defaultsError = "";
  let projectRules = { windowtitles: [], elements: [] };
  let globalPatterns = [];
  let patternsLoading = true;
  let patternsError = "";
  let showGuide = false;
  let guideStep = 0;
  let guideSteps = [];
  let guideTab = null;
  let projectReady = false;

  $: if (selected !== guideTab) showGuide = false;
  $: if (showGuide && tab.project && !projectReady && guideStep > 0) guideStep = 0;

  function startGuide() {
    const root = '[data-guide="global-anonymization"]';
    const subject = tab.kind === "elements" || tab.kind === "safeelements" ? "UI element" : "window title";
    const editor = `${root} ${tab.safe ? ".safe-titles" : ".global-anon"}`;
    guideSteps = [
      {
        target: tab.project ? `${root} .project-filter` : `${root} .switcher .grid_container`,
        highlightSelector: tab.project ? ".bx--list-box, .bx--list-box__menu" : undefined,
        title: tab.label,
        text: tab.project
          ? `Select a project to manage its ${subject} rules. Once its rules have loaded, choose Next.`
          : tab.safe
            ? `Review ${tab.kind === "safetitles" ? "window titles" : "UI text"} that can remain visible.`
            : tab.patterns
              ? "Explore the global anonymization patterns. These are read only."
              : `Manage default ${subject} rules across projects.`,
        allowInteraction: Boolean(tab.project),
      },
      {
        target: `${editor} .toolbar`,
        title: "Find entries",
        text: tab.safe
          ? "Use the filters and search to find entries awaiting review."
          : tab.patterns
            ? "Search for a pattern by name or content."
            : subject === "UI element"
              ? "Filter by application, class or type, or search for a rule."
              : "Filter by application or search for a rule.",
      },
      {
        target: editor,
        highlightSelector: tab.safe || tab.patterns ? ".table-scroll" : ".toolbar, .row-actions",
        title: tab.safe ? "Review entries" : tab.patterns ? "Explore patterns" : "Manage rules",
        text: tab.safe
          ? `Approve ${tab.kind === "safetitles" ? "titles" : "text"} without personal or sensitive data, or remove entries from the allowlist.`
          : tab.patterns
            ? "Compare the search pattern and replacement to see what gets anonymized."
            : tab.project
              ? "Add, edit or delete rules for this project."
              : "Add a rule, or edit or delete an existing one. Use Import and Export at the top of the page to work with CSV files.",
      },
    ];
    guideTab = selected;
    guideStep = 0;
    showGuide = true;
  }

  $: tab = tabs[selected];
  $: kind = tab.kind;
  $: readonly = Boolean(defaultsLoading || defaultsError || tab.patterns || tab.safe || tab.project || tab.planned);

  async function requestDefaults(ruleKind, options = {}, suffix = "") {
    const response = await fetchWithSession(`/api/damprojects/admin/default-anonymization-rules/${ruleKind}${suffix}`, {
      suppressErrorToast: true,
      suppressSuccessToast: true,
      ...options,
    });
    const data = await response.json().catch(() => ({ error: "Default rules are unavailable. Please retry after the backend is ready." }));
    if (!response.ok) {
      const error = new Error(data.error || "Unable to save default rules.");
      error.details = data.details;
      throw error;
    }
    return data;
  }

  async function loadDefaults() {
    defaultsLoading = true;
    defaultsError = "";
    try {
      const [windowtitles, elements] = await Promise.all([
        requestDefaults("windowtitles"), requestDefaults("elements"),
      ]);
      drafts = { windowtitles, elements };
    } catch (error) {
      defaultsError = error.message;
    } finally {
      defaultsLoading = false;
    }
  }

  // The patterns come from the same file the pipeline reads.
  async function loadPatterns() {
    patternsLoading = true;
    patternsError = "";
    try {
      const response = await fetchWithSession("/api/damprojects/admin/global-regex-patterns", {
        suppressErrorToast: true,
        suppressSuccessToast: true,
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !Array.isArray(data)) {
        throw new Error(data.error || "Global regex patterns are unavailable. Please try again later.");
      }
      // Same row shape as the other rule tables, the type becomes its placeholder.
      globalPatterns = data.map((pattern, index) => ({
        id: `global-pattern-${index + 1}`,
        name: pattern.name,
        applicationname: "",
        search: pattern.search,
        replace: `<<<${pattern.type}>>>`,
      }));
    } catch (error) {
      patternsError = error.message;
    } finally {
      patternsLoading = false;
    }
  }

  onMount(() => {
    loadDefaults();
    loadPatterns();
    return registerViewTour(startGuide);
  });

  function persistence(ruleKind) {
    return {
      save: (rule) => requestDefaults(ruleKind, { method: "POST", body: JSON.stringify(rule) }),
      remove: async (rule) => {
        await requestDefaults(ruleKind, { method: "DELETE" }, `/${rule.id}`);
        return true;
      },
    };
  }

  function csvCell(value) {
    const text = String(value ?? "");
    return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
  }

  function parseCsv(text) {
    const rows = [];
    let row = [];
    let cell = "";
    let quoted = false;
    let line = 1;
    let rowLine = 1;
    let closedQuote = false;
    for (let i = 0; i < text.length; i++) {
      const char = text[i];
      if (closedQuote && ![",", "\n", "\r"].includes(char)) {
        throw new Error(`CSV line ${line}: unexpected text after closing quote. Nothing imported.`);
      }
      if (quoted) {
        if (char === '"' && text[i + 1] === '"') {
          cell += '"';
          i++;
        } else if (char === '"') {
          quoted = false;
          closedQuote = true;
        }
        else cell += char;
        if (char === "\n" || (char === "\r" && text[i + 1] !== "\n")) line++;
      } else if (char === '"') {
        if (cell) throw new Error(`CSV line ${line}: unexpected quote. Nothing imported.`);
        quoted = true;
      }
      else if (char === ",") {
        row.push(cell);
        cell = "";
        closedQuote = false;
      } else if (char === "\n" || char === "\r") {
        if (char === "\r" && text[i + 1] === "\n") i++;
        row.push(cell);
        rows.push({ cells: row, line: rowLine });
        row = [];
        cell = "";
        closedQuote = false;
        rowLine = ++line;
      } else cell += char;
    }
    if (quoted) throw new Error(`CSV line ${rowLine}: unclosed quoted field. Nothing imported.`);
    if (cell || row.length || closedQuote) rows.push({ cells: [...row, cell], line: rowLine });
    return rows.filter(({ cells }) => cells.some((value) => value.trim()));
  }

  export function exportCsv() {
    const columns = csvColumns[kind];
    const lines = [
      columns.join(","),
      ...(tab.planned
        ? []
        : tab.safe
        ? kind === "safeelements" ? safeElements : safeTitles
        : tab.project
          ? projectRules[kind]
          : tab.patterns ? globalPatterns : drafts[kind]
      ).map((rule) =>
        columns.map((column) => csvCell(rule[column])).join(","),
      ),
    ];
    // Explicit UTF-8 blob with BOM, downloadjs would write non-ASCII strings as Latin-1.
    download(
      new Blob(["﻿", lines.join("\r\n")], { type: "text/csv;charset=utf-8" }),
      `default-anonymization-rules-${tab.file}.csv`,
      "text/csv",
    );
  }

  // Ergänzt die Regeln des aktiven Tabs. Duplikate (gleicher Name, da Namen
  // eindeutig sein müssen, oder gleiche Regel unter anderem Namen) werden übersprungen.
  export async function importCsv(file) {
    if (readonly) throw new Error("Built-in rules are read only.");
    // Keep the import bound to its original tab while the file is being read.
    const importKind = kind;
    const [header = { cells: [] }, ...rows] = parseCsv(
      (await file.text()).replace(/^\uFEFF/, ""),
    );
    const columns = header.cells.map((column) => column.trim().toLowerCase());
    if (new Set(columns).size !== columns.length) throw new Error("Duplicate CSV columns. Nothing imported.");
    const missing = csvColumns[importKind].filter(
      (column) => column !== "id" && !columns.includes(column),
    );
    if (missing.length) throw new Error(`Missing CSV columns: ${missing.join(", ")}`);
    const ruleColumns = csvColumns[importKind].filter(
      (column) => column !== "id" && column !== "name",
    );
    const ruleKey = (rule) =>
      JSON.stringify(ruleColumns.map((column) => String(rule[column] ?? "")));
    const names = new Set(drafts[importKind].map((rule) => String(rule.name).trim()));
    const keys = new Set(drafts[importKind].map(ruleKey));
    const ids = new Set(drafts[importKind].map((rule) => rule.id));
    const imported = [];
    const errors = [];
    const fields = importKind === "windowtitles"
      ? ["search", "replace"]
      : ["namesearch", "namereplace", "automationsearch", "automationreplace"];
    for (const { cells, line } of rows) {
      if (cells.length !== columns.length) {
        errors.push(`Line ${line}: expected ${columns.length} columns, found ${cells.length}`);
        continue;
      }
      const rule = Object.fromEntries(
        csvColumns[importKind].map((column) => [
          column,
          cells[columns.indexOf(column)] ?? "",
        ]),
      );
      rule.name = rule.name.trim();
      const rowErrors = [];
      if (!rule.name) rowErrors.push("name is required");
      if (importKind === "windowtitles" ? !rule.search.trim() : !rule.namesearch.trim() && !rule.automationsearch.trim()) {
        rowErrors.push("a search pattern is required");
      }
      for (const field of fields) {
        const error = anonymizationFieldError(field, rule);
        if (error) rowErrors.push(`${field}: ${error}`);
      }
      if (rowErrors.length) {
        errors.push(`Line ${line}: ${rowErrors.join(", ")}`);
        continue;
      }
      if (names.has(rule.name) || keys.has(ruleKey(rule))) continue;
      if (!rule.id || ids.has(rule.id)) rule.id = crypto.randomUUID();
      names.add(rule.name);
      keys.add(ruleKey(rule));
      ids.add(rule.id);
      imported.push(rule);
    }
    if (errors.length) {
      const remaining = errors.length > 5 ? `; and ${errors.length - 5} more invalid rows` : "";
      const error = new Error(`Nothing imported. ${errors.slice(0, 5).join("; ")}${remaining}.`);
      error.details = errors;
      throw error;
    }
    if (!imported.length) return { imported: 0, skipped: rows.length };
    const result = await requestDefaults(importKind, {
      method: "POST", body: JSON.stringify({ rules: imported }),
    });
    drafts[importKind] = result.rules;
    return { imported: result.imported, skipped: rows.length - imported.length + result.skipped };
  }
</script>

<div class="default-anonymization-rules" data-guide="global-anonymization">
  <div class="switcher">
    <ContentSwitcher options={tabs.map((item) => item.label)} bind:selected fitContent />
  </div>
  {#key selected}
    {#if selected === 0}
      <InfoBlock type="info" heading="Title allowlist" hideIcon>
        <div slot="message">
          Review and approve generic window titles, such as File Explorer or Open File. Only approve titles without personal or sensitive data.
        </div>
      </InfoBlock>
      <SafeTitleList bind:entries={safeTitles} />
    {:else if selected === 1}
      <InfoBlock type="info" heading="UI allowlist" hideIcon>
        <div slot="message">Review and approve generic UI text. Only approve text without personal or sensitive data.</div>
      </InfoBlock>
      <SafeTitleList kind="elements" bind:entries={safeElements} />
    {:else if selected === 2}
      <InfoBlock type="info" heading="Global regex patterns" hideIcon>
        <div slot="message">
          Browse and search the global anonymization patterns. These patterns cannot be edited here.
        </div>
      </InfoBlock>
      {#if patternsLoading}
        <p>Loading global regex patterns...</p>
      {:else if patternsError}
        <InfoBlock type="warning" heading="Unable to load global regex patterns" hideIcon showActions>
          <div slot="message">{patternsError}</div>
          <svelte:fragment slot="actions">
            <Button text="Retry" variant="secondary" size="sm" on:click={loadPatterns} />
          </svelte:fragment>
        </InfoBlock>
      {:else}
        <GlobalAnonRuleEditor kind="windowtitles" rules={globalPatterns} readonly hideApplication />
      {/if}
    {:else if selected === 3 || selected === 4}
      <InfoBlock type="info" heading={selected === 3 ? "Title default rules" : "UI default rules"} hideIcon>
        <div slot="message">
          Add, edit or delete default anonymization rules for {selected === 3 ? "window titles" : "UI elements"} across projects. Import or export rules as CSV.
        </div>
      </InfoBlock>
      {#if defaultsLoading}
        <p>Loading default rules...</p>
      {:else if defaultsError}
        <InfoBlock type="warning" heading="Unable to load default rules" hideIcon showActions>
          <div slot="message">{defaultsError}</div>
          <svelte:fragment slot="actions">
            <Button text="Retry" variant="secondary" size="sm" on:click={loadDefaults} />
          </svelte:fragment>
        </InfoBlock>
      {:else if selected === 3}
        <DefaultWindowTitleRules bind:rules={drafts.windowtitles} persist={persistence("windowtitles")} />
      {:else}
        <DefaultUiElementRules bind:rules={drafts.elements} persist={persistence("elements")} />
      {/if}
    {:else if selected === 5}
      <InfoBlock type="info" heading="Title project rules" hideIcon>
        <div slot="message">
          Select a project to view, add, edit or delete its window title anonymization rules.
        </div>
      </InfoBlock>
      <ProjectAnonRules kind="windowtitles" bind:rules={projectRules.windowtitles} bind:ready={projectReady} />
    {:else}
      <InfoBlock type="info" heading="UI project rules" hideIcon>
        <div slot="message">
          Select a project to view, add, edit or delete its UI element anonymization rules.
        </div>
      </InfoBlock>
      <ProjectAnonRules kind="elements" bind:rules={projectRules.elements} bind:ready={projectReady} />
    {/if}
  {/key}
</div>

<SpotlightGuide
  bind:open={showGuide}
  bind:currentStep={guideStep}
  steps={guideSteps}
  allowInteraction={false}
/>

<style>
  .default-anonymization-rules { display: flex; flex-direction: column; gap: var(--control-gap); min-width: 0; }
  .switcher { max-width: 100%; overflow-x: auto; }
</style>
